/** @jsx jsx */
// @flow

import React, { useState, useRef, useContext, useCallback } from 'react';
import { Query } from 'react-apollo';
import cn from 'classnames';
import { jsx } from '@emotion/core';
import GridApi from '../../components/GridApi';
import PageContainer from '../../components/PageContainer';
import ActionsHeader from '../../components/ActionsHeader';
import ModuleQuery from '../../components/ModuleQuery';
import Row from '../../components/Row';
import Col from '../../components/Col';
import Notifications from '../../components/Notifications';
import ContentChecker from '../../components/ContentChecker';
import BlockingStatus from '../../components/BlockingStatus';
import Curtain from '../../components/Curtain';
import ActionsOrganizer from './components/ActionsOrganizer';
import GridApiController from '../../controllers/GridApiController';
import userSettingsQuery from '../../graphql/queries/userSettings.query';
import moduleDataQuery from '../../graphql/queries/accountsData.query';
import formatters from './formatters';
import { OrderContentConsumer } from '../../context/orderContent.context';
import { getDefaultUserSettings } from '../../utils'
import { MobileView, useMobileMediaQuery } from '../../config/mediaQuery';
import ModuleMenuBar from '../../components/ModuleMenuBar';
import FullScreenContainer from '../../components/FullScreenContainer';
import { ActionsContext } from '../../context/actions.context';
import useMenuHandler from '../../hooks/useMenuHandler';

type Props = {
  isFullPage: boolean,
  showGroupPanel: boolean,
  active: boolean,
};

const App = (props: Props) => {
  const moduleName = 'accounts';

  const menuRef = useRef();
  const menuButtonRef = useRef();
  const { allScreens = [] } = useContext(ActionsContext);

  const { isFullPage = true, showGroupPanel = false, active = false } = props;

  const isMobile = useMobileMediaQuery();
  const [displayMenu, setDisplayMenu] = useState(false);
  const [handlerAdded, setHandlerAdded] = useState(false);
  
  // State to track the displayed view name for UI updates
  // - undefined: initial state, will sync from query on first load
  // - null: user selected "Default View"
  // - string: user selected a named view
  const [displayedViewName, setDisplayedViewName] = useState(undefined);

  const changeDisplayMenu = (status) => {
    setDisplayMenu(status);
  }

  const handler = useCallback((e) => {
    if (menuRef.current
      && menuButtonRef.current
      && !menuRef.current.contains(e.target)
      && !menuButtonRef.current.contains(e.target)
    ) {
      setDisplayMenu(value => {
        if (value) {
          return false;
        }
        return value;
      });
    }
  }, []);

  useMenuHandler(handler, active && allScreens.length === 0, setHandlerAdded);
  
  // Handler for when a view is loaded - just updates the UI display name
  // The actual view settings are applied by onApplyViewSettings in ActionsHeader
  const handleViewLoaded = (viewName) => {
    setDisplayedViewName(viewName);
  };

  return (
    <PageContainer
      className={cn({
        'page--is-full-page': isFullPage,
        'page--is-nested': !isFullPage,
        'page--with-group-panel': showGroupPanel,
      })}
      isFullPage={isFullPage}
      active={active}
    >
      <FullScreenContainer />
      <OrderContentConsumer>
        {({
          selectedOrderType,
          selectedOrderDate,
          onChangeOrderItems,
        }: any) => {
          return (
            <ModuleQuery
              query={moduleDataQuery}
              variables={{
                moduleName: moduleName,
              }}
            >
              {({
                data: moduleData,
                loading: moduleLoading,
                refetch: moduleRefetch,
                refetching: moduleRefetching,
                refetched: moduleRefetched,
              }) => (
                <Query
                  query={userSettingsQuery}
                  variables={{
                    moduleName: moduleName,
                  }}
                >
                  {({ data: dataGrid, loading }: any) => {
                    if (!dataGrid || loading) {
                      return (
                        <BlockingStatus
                          excludeHeader
                          active
                          container={document.body}
                          text="Loading..."
                        />
                      );
                    }

                    // Get view name from query - null means Default View
                    const queryViewName = dataGrid && dataGrid.userSettings && dataGrid.userSettings.viewName
                      ? dataGrid.userSettings.viewName
                      : null;

                    // Build userSettings based on whether we have a named view or Default View
                    // IMPORTANT: When viewName is null (Default View), we should NOT pass
                    // the server-returned columns/sorting/groups/frozenColumns/filterDefinition
                    // because those are empty/stale for Default View. Instead, we let
                    // GridApiController fall back to the default settings.
                    // 
                    // This fixes the bug where loading Default View on initial page refresh
                    // would not apply default settings correctly, while switching from a
                    // named view back to Default View would work (because onViewReset is
                    // explicitly called with default values).
                    let userSettings;
                    if (queryViewName) {
                      // Named view - use server-returned settings
                      userSettings = dataGrid && dataGrid.userSettings
                        ? {
                            columnsMapping: dataGrid.userSettings.columns,
                            frozenColumns: dataGrid.userSettings.frozenColumns,
                            groups: dataGrid.userSettings.groups,
                            sorting: dataGrid.userSettings.sorting,
                            filterDefinition: dataGrid.userSettings.filterDefinition,
                          }
                        : {};
                    } else {
                      // Default View - don't pass view-specific settings
                      // Let GridApiController use defaultColumns, defaultSorting, defaultGroupPanels
                      // Note: columnsMapping, frozenColumns, groups, sorting, filterDefinition
                      // are intentionally omitted so defaults are applied
                      userSettings = {};
                    }

                    const defaultUserSettings = getDefaultUserSettings(dataGrid)
                    
                    // Only sync displayedViewName from query on initial load (when displayedViewName is undefined)
                    // After initial load, displayedViewName is managed entirely by user selection
                    if (displayedViewName === undefined) {
                      // Use setTimeout to avoid setState during render
                      setTimeout(() => { setDisplayedViewName(queryViewName); }, 0);
                    }
                    
                    // Determine what to show in the UI:
                    // - If displayedViewName is undefined (initial load), use queryViewName
                    // - Otherwise use displayedViewName (which could be null for Default View)
                    const currentViewNameForUI = displayedViewName === undefined ? queryViewName : displayedViewName;

                    return (
                      <GridApiController
                        data={moduleData.accountsContent}
                        loading={loading || moduleLoading}
                        refetching={moduleRefetching}
                        refetched={moduleRefetched}
                        refetch={moduleRefetch}
                        defaultColumns={defaultUserSettings.defaultColumns}
                        defaultSorting={defaultUserSettings.defaultSorting}
                        onChangeOrderItems={onChangeOrderItems}
                        defaultGroupPanels={defaultUserSettings.defaultGroupPanels}
                        active={active}
                        isMobile={isMobile}
                        // shouldKeepCollection
                        {...userSettings}
                      >
                        {({
                          selectedItems,
                          gridUpdated,
                          collection,
                          columnsMapping,
                          currentItem,
                          onGridUpdated,
                          onGridWillUnmount,
                          onGridRef,
                          onColSorted,
                          onColResized,
                          onColDragged,
                          onColFreezed,
                          onRowFreezed,
                          onRowSelected,
                          onColumnsMappingChange,
                          onCurrentItemChanged,
                          onGroupPanelChanged,
                          onClearSelection,
                          onNotesSaved,
                          saveStatus,
                          orderCreateStatus,
                          orderActiveStatus,
                          onOrderCreated,
                          onOrderActivated,
                          onViewSaved,
                          onViewReset,
                          onApplyViewSettings,
                          grid,
                          userViewState,
                          refetching,
                          hasAllColumnsHidden,
                          frozenColumns,
                          refetch,
                          isLoading,
                          isRefetching,
                          isCollapseLoading,
                          setCollapseLoading,
                          hasDataToDisplay,
                          clearCollectionAndResetChangedOrderItems,
                          playResetAnimation,
                          handleResetAnimation,
                          setFlexGridFilter,
                          getGridFilterDefinition,
                          clearInternalFilters,
                          toggleMultiSort,
                          multiColumnSort
                        }) => {
                          return (
                            <React.Fragment>
                              {/* Show loading indicator during collapse/expand operations */}
                              {isCollapseLoading && (
                                <BlockingStatus
                                  excludeHeader
                                  active
                                  container={document.body}
                                  text="Processing..."
                                />
                              )}
                              <Notifications
                                orderCreateStatus={orderCreateStatus}
                                orderActiveStatus={orderActiveStatus}
                                saveStatus={saveStatus}
                              />

                              <ActionsHeader
                                grid={grid}
                                collection={collection}
                                defaultColumnsMapping={
                                  defaultUserSettings.defaultColumns
                                }
                                columnsMapping={columnsMapping}
                                selectedItems={selectedItems}
                                moduleName={moduleName}
                                saveStatus={saveStatus}
                                loading={isLoading || isRefetching}
                                onViewSaved={onViewSaved}
                                onOrderCreated={onOrderCreated}
                                onOrderActivated={onOrderActivated}
                                onViewReset={onViewReset}
                                onApplyViewSettings={onApplyViewSettings}
                                onColumnsMappingChange={
                                  onColumnsMappingChange
                                }
                                userViewState={userViewState}
                                refetch={refetch}
                                hasAllColumnsHidden={hasAllColumnsHidden}
                                hasDataToDisplay={hasDataToDisplay}
                                frozenColumns={frozenColumns}
                                currentItem={currentItem}
                                orderCreateStatus={orderCreateStatus}
                                orderActiveStatus={orderActiveStatus}
                                useLegacyColumnsManager={true}
                                defaultSettings={defaultUserSettings}
                                handleResetAnimation={handleResetAnimation}
                                toggleMultiSort={toggleMultiSort}
                                multiColumnSort={multiColumnSort}
                                // Multiple saved views feature props
                                useMultipleViews={true}
                                currentViewName={currentViewNameForUI}
                                onViewLoaded={handleViewLoaded}
                                getGridFilterDefinition={getGridFilterDefinition}
                              >
                                {actions => {
                                  const { ColumnsAction, isOneRowSelected } = actions;
                                  return (
                                    <>
                                      <MobileView>
                                        <ModuleMenuBar
                                          moduleRefetch={moduleRefetch}
                                          changeDisplayMenu={changeDisplayMenu}
                                          ColumnsAction={ColumnsAction}
                                          displayMenu={displayMenu}
                                          menuButtonRef={menuButtonRef}
                                          clearInternalFilters={clearInternalFilters}
                                        />
                                      </MobileView>
                                      <div ref={menuRef}>
                                        <Row
                                          css={{
                                            justifyContent: 'space-between',
                                            padding: '0 10px',
                                            ...(isMobile && {
                                              position: 'absolute',
                                              bottom: 0,
                                              boxShadow:
                                                '0px -4px 4px 0px #ACACAC40',
                                              zIndex: 100,
                                              background: '#fff',
                                              borderRadius: '8px 8px 0px 0px',
                                              width: '100%',
                                              display: displayMenu || isOneRowSelected
                                                ? 'flex'
                                                : 'none',
                                              justifyContent: 'center',
                                              height: (displayMenu && '189px') || (isOneRowSelected && '240px'),
                                            }),
                                          }}
                                        >
                                          <Col>
                                            <ActionsOrganizer
                                              onNotesSaved={onNotesSaved}
                                              minHeight="80px"
                                              selectedOrderType={selectedOrderType}
                                              selectedOrderDate={selectedOrderDate}
                                              clearCollectionAndResetChangedOrderItems={
                                                clearCollectionAndResetChangedOrderItems
                                              }
                                              clearInternalFilters={clearInternalFilters}
                                              setCollapseLoading={setCollapseLoading}
                                              {...actions}
                                              displayRowMenu={isOneRowSelected}
                                              displayMenu={displayMenu}
                                              toggleMultiSort={toggleMultiSort}
                                            />
                                          </Col>
                                        </Row>
                                      </div>
                                    </>
                                  )
                                }}
                              </ActionsHeader>
                              <ContentChecker
                                isLoading={isLoading}
                                isRefetching={isRefetching}
                                hasAllColumnsHidden={hasAllColumnsHidden}
                                hasDataToDisplay={hasDataToDisplay}
                                collection={collection}
                              >
                                {() => {
                                  return (
                                    <React.Fragment>
                                      <Curtain
                                        container={grid && grid.hostElement}
                                        active={active}
                                      />
                                      <GridApi
                                        collection={collection}
                                        columnsMapping={columnsMapping}
                                        frozenColumns={frozenColumns}
                                        onRowSelected={onRowSelected}
                                        onColSorted={onColSorted}
                                        onColResized={onColResized}
                                        onColDragged={onColDragged}
                                        onColFreezed={onColFreezed}
                                        onRowFreezed={onRowFreezed}
                                        onGridUpdated={onGridUpdated}
                                        onGridWillUnmount={onGridWillUnmount}
                                        onCurrentItemChanged={
                                          onCurrentItemChanged
                                        }
                                        onGroupPanelChanged={
                                          onGroupPanelChanged
                                        }
                                        onClearSelection={onClearSelection}
                                        ref={onGridRef}
                                        refetching={isRefetching}
                                        hasAllColumnsHidden={
                                          hasAllColumnsHidden
                                        }
                                        hasDataToDisplay={hasDataToDisplay}
                                        formatters={formatters}
                                        showFrozenBars
                                        showGroupPanel={
                                          showGroupPanel
                                        }
                                        active={active}
                                        playResetAnimation={playResetAnimation}
                                        setFlexGridFilter={setFlexGridFilter}
                                        menuRef={menuRef}
                                        isMobile={isMobile}
                                        isFullPage={isFullPage}
                                        moduleName={moduleName}
                                        handlerAdded={handlerAdded}
                                        multiColumnSort={multiColumnSort}
                                      />
                                    </React.Fragment>
                                  );
                                }}
                              </ContentChecker>
                            </React.Fragment>
                          );
                        }}
                      </GridApiController>
                    );
                  }}
                </Query>
              )}
            </ModuleQuery>
          );
        }}
      </OrderContentConsumer>
    </PageContainer>
  );

}

export default App;
